import { generateDeviceId } from '@1debit/device-intelligence';

import {
  getBrowserForTracking,
  getDeviceTypeForTracking,
} from 'consts/platformConsts';
import {
  AJS_ANONYMOUS_ID,
  Facebook_Click,
  Facebook_Pixel_Identifier,
} from 'consts/storageConsts';

import { deviceIntelligenceEnvironment } from 'utils/deviceIntelligenceUtils';
import {
  AD_CAMPAIGN_QSP,
  AF_ADVERTISING_ID_QSP,
  AF_ID_QSP,
  IS_EMBEDDED,
  IS_PRELOAD,
} from 'utils/queryParamHelpers';
import cookies from 'utils/storage/cookies';

function getAdditionalTrackingProperties() {
  const properties = {
    is_embedded: !!IS_EMBEDDED,
    device_type: getDeviceTypeForTracking(),
    browser: getBrowserForTracking(),
    ad_campaign_id: AD_CAMPAIGN_QSP || null,
    afid: AF_ID_QSP || null,
    af_advertising_id: AF_ADVERTISING_ID_QSP || null,
    fbc: cookies.get(Facebook_Click),
    fbp: cookies.get(Facebook_Pixel_Identifier),
    device_id: generateDeviceId({ env: deviceIntelligenceEnvironment }),
  };

  return properties;
}

function buildContextObject(integrationsDecisions, formData) {
  let anonymousID = cookies.get(AJS_ANONYMOUS_ID);
  return integrationsDecisions && integrationsDecisions.shouldSendToFacebook
    ? {
        traits: {
          firstName: formData?.firstName ?? '',
          lastName: formData?.lastName ?? '',
          email: formData?.email ?? '',
          phone: formData?.phone ?? '',
        },
        anonymousId: anonymousID,
      }
    : {
        integrations: {
          'Facebook Pixel': false,
          'Facebook Conversions API': false,
          'Facebook Offline Conversions': false,
        },
        anonymousId: anonymousID,
      };
}

export function track(
  eventName,
  eventProperties,
  callback = () => {},
  excludeAdditionalProperties,
  integrationsDecisions,
  formData = {}
) {
  const { analytics } = window;
  if (IS_PRELOAD) return;

  const additionalProperties = excludeAdditionalProperties
    ? {}
    : getAdditionalTrackingProperties();

  const context = buildContextObject(integrationsDecisions, formData);

  if (analytics) {
    analytics.track(
      eventName,
      { ...eventProperties, ...additionalProperties },
      context,
      callback
    );
  }
}

export function page(
  eventName,
  eventProperties,
  useAdditionalProps,
  integrationsDecisions
) {
  const { analytics } = window;

  if (IS_PRELOAD) return;

  // unless explicitly set to false,
  // addition props will be included in page event
  const additionalProperties =
    useAdditionalProps === false ? {} : getAdditionalTrackingProperties();

  const context = buildContextObject(integrationsDecisions);

  if (analytics) {
    analytics.page(
      eventName,
      {
        ...eventProperties,
        ...additionalProperties,
      },
      context
    );
  }
}

/**
 * @param {Object} - options {
          identifyId: id(string) to identify on (if absent, will identify on anonymous_id)
          traits: key-value object of user traits (ie email, name, etc)
        }
 */
export function identifyUser(options = {}, callback, integrationsDecisions) {
  const { analytics } = window;
  const { traits, identifyId } = options;
  const context = buildContextObject(integrationsDecisions);

  if (analytics) {
    if (identifyId) {
      analytics.identify(identifyId, traits, context, callback);
    } else {
      analytics.identify(traits, context, callback);
    }
  }
}

export function getUserTraits() {
  const { analytics } = window;

  return analytics?.user?.().traits();
}
